/**
 * The top-bar Netxol AI button (and Ctrl+J). Light on purpose: the panel itself (modules/ai/NetxolAI) is loaded on
 * demand — and preloaded when the browser is idle after sign-in, so it still opens at once.
 */
import { lazy, Suspense, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { core } from '@/shared/api/client';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { can, useAuth } from '@/shared/lib/auth';
import { t } from '@/i18n';
import { preloadAiPanel, useAiPanel } from './panel-store';

export function AiButton() {
  const user = useAuth((s) => s.user); const { toggle } = useAiPanel();
  useEffect(() => { const h = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); toggle(); } }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [toggle]);
  // detailed answers finished while the user was elsewhere: a dot on the button and one notice per answer
  const allowed = can(user, 'ai.use'); const nav = useNavigate(); const told = useRef(new Set<string>());
  const ready = useQuery({ queryKey: ['ai-ready'], enabled: allowed, refetchInterval: 60_000, retry: false, queryFn: async () => (await core.get('/ai/ready')).data as { id: string; title: string }[] });
  useEffect(() => {
    for (const c of ready.data ?? []) if (!told.current.has(c.id)) {
      told.current.add(c.id);
      toast(t('brain.ready_toast'), { description: c.title, action: { label: t('brain.open'), onClick: () => nav(`/ai/${c.id}`) } });
    }
  }, [ready.data, nav]);
  if (!allowed) return null;
  return <Button variant="ghost" size="sm" onClick={toggle} title={`${t('ai.name')} (Ctrl J)`} className="relative gap-1.5 font-semibold text-primary"><Sparkles className="size-4" /><span className="hidden lg:inline">{t('ai.name')}</span>
    {!!ready.data?.length && <span className="absolute end-1 top-1 size-2 rounded-full bg-rose-500" data-testid="ai-ready-dot" />}</Button>;
}

const LazyPanel = lazy(async () => ({ default: (await preloadAiPanel()).AiPanel }));

/** The side panel, loaded when first opened; its code is fetched in the background once the user can use Netxol AI. */
export function AiPanelSlot() {
  const user = useAuth((s) => s.user); const open = useAiPanel((s) => s.open);
  const allowed = can(user, 'ai.use');
  useEffect(() => {
    if (!allowed) return;
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (h: number) => void };
    const run = () => { void preloadAiPanel(); };
    if (w.requestIdleCallback) { const h = w.requestIdleCallback(run, { timeout: 5000 }); return () => w.cancelIdleCallback?.(h); }
    const h = setTimeout(run, 2000); return () => clearTimeout(h);
  }, [allowed]);
  if (!open) return null;
  return (
    <Suspense fallback={<div className="fixed inset-y-0 end-0 z-40 flex w-full max-w-[460px] items-center justify-center border-s bg-background text-xs text-muted-foreground shadow-2xl" data-testid="ai-panel-loading"><Loader2 className="me-2 size-4 animate-spin" />{t('common.loading')}</div>}>
      <LazyPanel />
    </Suspense>
  );
}
